import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Info, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Abas } from '../../componentes/abas';
import { Botao, Selo } from '../../componentes/base';
import { Alternador, Entrada, EntradaSensivel, Rotulo } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { textoLivre } from '../../nucleo/formato';
import { CAMINHO_CONFIGURACOES } from './crm-caminhos';
import { LinkVoltar } from '../../nucleo/voltar';

/**
 * Lote crm7 (ficha 2026-10-29 "configuracoes-do-crm-igual-a-origem"): as peças comuns das seções das Configurações do CRM,
 * como na origem (clone do RD). Cada seção abre em página inteira, sem o menu lateral de antes:
 *  - `CabecalhoConfig`: "Configurações" em cima, seta de voltar ao índice, título e o botão primário à direita, o texto de
 *    abertura e as abas (as que o MalgoOS ainda não tem aparecem desativadas, com o selo "Em breve", como no índice);
 *  - `LinhaAlternador`: a linha "Permitir que usuários criem..." com o alternador;
 *  - `ListaConfig`: a lista da origem (Fontes, Campanhas, Motivo de perda): "Adicionar X" abre o formulário no topo do
 *    cartão (Salvar e Cancelar), "Pesquisar...", tabela com seleção, apagar os selecionados ou todos e a lixeira na linha.
 */

export interface AbaConfig {
  chave: string;
  rotulo: string;
  /** Caminho da aba; sem ele, a aba aparece desativada com o selo "Em breve". */
  para?: string;
}

export function CabecalhoConfig({
  titulo,
  acao,
  intro,
  abas,
  ativa,
  voltar = CAMINHO_CONFIGURACOES,
  migalha,
}: {
  titulo: ReactNode;
  acao?: ReactNode;
  intro?: ReactNode;
  abas?: AbaConfig[];
  ativa?: string;
  voltar?: string;
  /** O texto acima do título (padrão: "Configurações"; no editor do modelo, "Modelos de proposta"). */
  migalha?: string;
}) {
  const { t } = useTranslation('pipeline');
  const ligadas = (abas ?? []).filter((a) => a.para);
  const breves = (abas ?? []).filter((a) => !a.para);
  return (
    <header className="flex min-w-0 flex-col gap-3" data-cabecalho-config="">
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-xs font-medium text-tinta-3">{migalha ?? t('crm.cfg.migalha')}</p>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <LinkVoltar
            para={voltar}
            aria-label={t('crm.cfg.voltar')}
            title={t('crm.cfg.voltar')}
            className="grid size-11 shrink-0 place-items-center rounded-lg text-tinta-2 hover:bg-sup-2 hover:text-tinta-1 md:size-9"
            data-voltar-config=""
          >
            <ArrowLeft className="size-5" aria-hidden />
          </LinkVoltar>
          <h1 className="min-w-0 flex-1 text-2xl font-bold tracking-tight break-words">{titulo}</h1>
          {acao && <div className="flex flex-wrap items-center gap-2">{acao}</div>}
        </div>
      </div>
      {intro && <div className="flex max-w-3xl flex-col gap-1 text-sm text-tinta-2">{intro}</div>}
      {abas && abas.length > 0 && (
        <div className="flex min-w-0 flex-wrap items-center gap-2" data-abas-config="">
          {ligadas.length > 0 && <Abas itens={ligadas.map((a) => ({ chave: a.chave, rotulo: a.rotulo, para: a.para }))} ativa={ativa ?? ligadas[0]!.chave} rotulo={String(titulo)} grupo="config-crm" />}
          {breves.map((a) => (
            <span key={a.chave} className="flex items-center gap-2 px-2 text-sm font-medium text-tinta-3" aria-disabled="true" title={t('crm.ficha2.config.emBreveAjuda')} data-aba-em-breve={a.chave}>
              {a.rotulo}
              <Selo>{t('crm.ficha2.config.emBreve')}</Selo>
            </span>
          ))}
        </div>
      )}
    </header>
  );
}

/** A linha com o alternador da origem ("Permitir que usuários criem novas fontes"). */
export function LinhaAlternador({ texto, ajuda, marcado, aoMudar, desabilitado, dado }: { texto: string; ajuda?: ReactNode; marcado: boolean; aoMudar: (v: boolean) => void; desabilitado?: boolean; dado?: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-borda bg-sup-1 p-4" data-linha-alternador={dado ?? ''}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{texto}</p>
        {ajuda && <div className="mt-1 text-xs text-tinta-3">{ajuda}</div>}
      </div>
      <Alternador marcado={marcado} aoMudar={aoMudar} rotulo={texto} desabilitado={desabilitado} />
    </div>
  );
}

/** A caixa de seleção das tabelas das configurações (a mesma da Tabela do sistema). */
export function CaixaConfig({ marcado, rotulo, aoMudar, desabilitado }: { marcado: boolean; rotulo: string; aoMudar: (v: boolean) => void; desabilitado?: boolean }) {
  return (
    <label className={`flex size-6 shrink-0 items-center justify-center ${desabilitado ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" className="size-4 cursor-pointer accent-acento-forte disabled:cursor-not-allowed" checked={marcado} disabled={desabilitado} aria-label={rotulo} onChange={(e) => aoMudar(e.target.checked)} />
    </label>
  );
}

export const CLASSE_TABELA_CONFIG = 'overflow-x-auto';
export const CLASSE_TH = 'px-3 py-2 text-left text-xs font-semibold text-tinta-3';
export const CLASSE_TD = 'px-3 py-2 align-middle';

export interface ItemListaConfig {
  chave: string;
  nome: string;
  descricao?: string;
}

export interface TextosListaConfig {
  /** "Adicionar Fonte". */
  adicionar: string;
  /** "Nome da Fonte" (o campo do formulário). */
  campo: string;
  placeholder: string;
  /** "Nome da Fonte" (a coluna; o total entra entre parênteses). */
  coluna: string;
  /** "fonte(s)" no "Apagar a(s) 2 fonte(s) selecionada(s)": recebe `count`. */
  apagarSelecionados: (n: number) => string;
  apagarTodos: (n: number) => string;
  confirmarApagar: (n: number) => string;
}

/**
 * A lista da origem (`lgList` do clone). Os itens saem em ordem alfabética; o nome repetido (sem acento e sem caixa) é
 * recusado aqui, antes do servidor. `aoRenomear` (opcional) põe o lápis na linha, com a edição na própria linha.
 */
export function ListaConfig({
  itens,
  textos,
  comDescricao,
  podeCriar,
  podeExcluir,
  aoCriar,
  aoExcluir,
  aoRenomear,
  colunasExtras = [],
  dado,
}: {
  itens: ItemListaConfig[];
  textos: TextosListaConfig;
  comDescricao?: boolean;
  podeCriar: boolean;
  podeExcluir: boolean;
  aoCriar: (v: { nome: string; descricao: string }) => Promise<unknown>;
  aoExcluir: (chaves: string[]) => Promise<unknown>;
  aoRenomear?: (chave: string, nome: string) => Promise<unknown>;
  colunasExtras?: { rotulo: string; celula: (i: ItemListaConfig) => ReactNode }[];
  dado: string;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const id = useId();
  const [adicionando, setAdicionando] = useState(false);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState<string[]>([]);
  const [apagar, setApagar] = useState<string[] | null>(null);
  const [editando, setEditando] = useState<{ chave: string; nome: string } | null>(null);
  const norm = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const ordenados = useMemo(() => [...itens].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })), [itens]);
  const visiveis = ordenados.filter((i) => !busca.trim() || norm(i.nome).includes(norm(busca)));
  const selValida = sel.filter((c) => itens.some((i) => i.chave === c));
  const todosMarcados = visiveis.length > 0 && visiveis.every((i) => selValida.includes(i.chave));
  const fechar = () => (setAdicionando(false), setNome(''), setDescricao(''), setErro(null));
  const salvar = async () => {
    const v = nome.trim();
    if (!v) return setErro(tn('erros.obrigatorio'));
    if (itens.some((i) => norm(i.nome) === norm(v))) return setErro(t('crm.cfg.lista.repetido'));
    setSalvando(true);
    try {
      await aoCriar({ nome: v, descricao: descricao.trim() });
      fechar();
    } catch {
      // O aviso do erro sai pelo toast de quem chamou; o formulário fica aberto com o que foi digitado.
    } finally {
      setSalvando(false);
    }
  };
  const colunas = 3 + (comDescricao ? 1 : 0) + colunasExtras.length;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-lista-config={dado}>
      {podeCriar && !adicionando && (
        <Botao variante="primario" className="self-start" icone={<Plus className="size-4" />} onClick={() => setAdicionando(true)} data-adicionar-config={dado}>
          {textos.adicionar}
        </Botao>
      )}
      <div className="min-w-0 rounded-xl border border-borda bg-sup-1">
        {adicionando && (
          <form
            className="flex flex-wrap items-end gap-3 border-b border-borda p-4"
            data-form-lista-config={dado}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void salvar();
            }}
          >
            <div className="w-full sm:w-64">
              <Rotulo id={`${id}-nome`} rotulo={textos.campo} obrigatorio erro={erro ?? undefined}>
                <Entrada id={`${id}-nome`} autoFocus maxLength={120} placeholder={textos.placeholder} value={nome} aria-invalid={!!erro} onChange={(e) => (setNome(e.target.value), setErro(null))} data-nome-lista-config="" />
              </Rotulo>
            </div>
            {comDescricao && (
              <div className="w-full sm:min-w-64 sm:flex-1">
                <Rotulo id={`${id}-desc`} rotulo={t('crm.cfg.lista.descricao')}>
                  <EntradaSensivel mascara={null} livre id={`${id}-desc`} maxLength={300} placeholder={t('crm.cfg.lista.descricaoPlaceholder')} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
                </Rotulo>
              </div>
            )}
            <div className="flex gap-2">
              <Botao type="submit" variante="primario" carregando={salvando} data-salvar-lista-config="">
                {tn('comum.salvar')}
              </Botao>
              <Botao variante="fantasma" onClick={fechar}>
                {tn('comum.cancelar')}
              </Botao>
            </div>
          </form>
        )}
        <div className="relative border-b border-borda p-3">
          <label htmlFor={`${id}-busca`} className="sr-only">
            {t('crm.cfg.lista.pesquisar')}
          </label>
          <Entrada id={`${id}-busca`} type="search" placeholder={t('crm.cfg.lista.pesquisar')} value={busca} onChange={(e) => setBusca(e.target.value)} className="pr-10" data-pesquisar-config="" />
          <Search className="pointer-events-none absolute top-1/2 right-6 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
        </div>
        {podeExcluir && selValida.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-borda bg-sup-2 px-3 py-2" data-lote-config="">
            <Botao tamanho="sm" variante="fantasma" icone={<Trash2 className="size-4" />} onClick={() => setApagar(selValida)} data-apagar-selecionados="">
              {textos.apagarSelecionados(selValida.length)}
            </Botao>
            <Botao tamanho="sm" variante="perigo" onClick={() => setApagar(itens.map((i) => i.chave))} data-apagar-todos="">
              {textos.apagarTodos(itens.length)}
            </Botao>
          </div>
        )}
        <div className={CLASSE_TABELA_CONFIG} role="region" tabIndex={0} aria-label={textos.coluna}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-borda">
                <th className={`${CLASSE_TH} w-10`}>{podeExcluir && <CaixaConfig marcado={todosMarcados} rotulo={t('crm.cfg.lista.selecionarTodos')} aoMudar={(v) => setSel(v ? visiveis.map((i) => i.chave) : [])} />}</th>
                <th className={CLASSE_TH}>
                  {textos.coluna} ({itens.length})
                </th>
                {comDescricao && <th className={CLASSE_TH}>{t('crm.cfg.lista.descricao')}</th>}
                {colunasExtras.map((c) => (
                  <th key={c.rotulo} className={CLASSE_TH}>
                    {c.rotulo}
                  </th>
                ))}
                <th className={`${CLASSE_TH} text-right`}>{t('crm.cfg.lista.opcoes')}</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.length === 0 && (
                <tr>
                  <td colSpan={colunas} className="px-3 py-6 text-center text-tinta-3">
                    {t('crm.cfg.lista.vazio')}
                  </td>
                </tr>
              )}
              {visiveis.map((i) => (
                <tr key={i.chave} className="border-b border-borda last:border-b-0" data-item-config={i.nome}>
                  <td className={CLASSE_TD}>{podeExcluir && <CaixaConfig marcado={selValida.includes(i.chave)} rotulo={t('crm.cfg.lista.selecionar', { nome: i.nome })} aoMudar={(v) => setSel((s) => (v ? [...s, i.chave] : s.filter((x) => x !== i.chave)))} />}</td>
                  <td className={`${CLASSE_TD} font-medium`}>
                    {editando?.chave === i.chave && aoRenomear ? (
                      <form
                        className="flex items-center gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const v = editando.nome.trim();
                          if (v && v !== i.nome) void aoRenomear(i.chave, v).then(() => setEditando(null), () => undefined);
                          else setEditando(null);
                        }}
                      >
                        <Entrada autoFocus aria-label={textos.campo} maxLength={120} value={editando.nome} onChange={(e) => setEditando({ chave: i.chave, nome: e.target.value })} data-renomear-config="" />
                        <Botao type="submit" tamanho="sm" variante="primario">
                          {tn('comum.salvar')}
                        </Botao>
                        <Botao tamanho="sm" variante="fantasma" onClick={() => setEditando(null)}>
                          {tn('comum.cancelar')}
                        </Botao>
                      </form>
                    ) : (
                      <span className="break-words">{i.nome}</span>
                    )}
                  </td>
                  {comDescricao && <td className={`${CLASSE_TD} text-tinta-2`}>{i.descricao ? textoLivre(i.descricao) : ''}</td>}
                  {colunasExtras.map((c) => (
                    <td key={c.rotulo} className={CLASSE_TD}>
                      {c.celula(i)}
                    </td>
                  ))}
                  <td className={CLASSE_TD}>
                    <span className="flex justify-end gap-1">
                      {aoRenomear && podeCriar && (
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.cfg.lista.editar', { nome: i.nome })} title={t('crm.cfg.lista.editar', { nome: i.nome })} icone={<Pencil className="size-4" />} onClick={() => setEditando({ chave: i.chave, nome: i.nome })} />
                      )}
                      {podeExcluir && (
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.cfg.lista.excluir', { nome: i.nome })} title={t('crm.cfg.lista.excluir', { nome: i.nome })} icone={<Trash2 className="size-4" />} onClick={() => setApagar([i.chave])} data-excluir-config={i.nome} />
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Confirmar
        aberto={!!apagar}
        aoFechar={() => setApagar(null)}
        perigoso
        titulo={apagar?.length === 1 ? t('crm.cfg.lista.confirmarUm', { nome: itens.find((i) => i.chave === apagar[0])?.nome ?? '' }) : textos.confirmarApagar(apagar?.length ?? 0)}
        texto={t('crm.cfg.lista.confirmarTexto')}
        aoConfirmar={() => {
          const c = apagar ?? [];
          setApagar(null);
          void aoExcluir(c).then(
            () => setSel((s) => s.filter((x) => !c.includes(x))),
            () => undefined,
          );
        }}
      />
    </div>
  );
}

/** Um aviso no padrão da origem (o "pralert"): os Questionários levam à Qualificação por ele. */
export function AvisoConfig({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 rounded-xl border border-borda border-l-4 border-l-ciano bg-sup-1 p-4 text-sm" data-aviso-config="">
      <Info className="mt-0.5 size-4 shrink-0 text-ciano" aria-hidden />
      <div className="min-w-0">
        <p className="font-semibold">{titulo}</p>
        <div className="text-tinta-2">{children}</div>
      </div>
    </div>
  );
}
